import React, { useState } from "react";
import { motion } from "framer-motion"; //  Import Framer Motion
import {
  loginUser,
  resetPassword,
  resendVerificationEmail,
} from "services/authService";
import { authErrorMessage, reportAuthError } from "services/authErrors";
import { useAuth } from "context/AuthProvider";
import useCooldown from "Hooks/useCooldown";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthHeader from "Pages/Auth/components/AuthHeader";

const RESEND_DELAY = 60; // seconds between two emails of the same kind

const inputClass =
  "w-full p-2 border rounded-lg bg-white text-black light-input focus:outline-none focus:ring-2 focus:ring-blue-400";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  // Login was refused because the email is not confirmed yet
  const [notVerified, setNotVerified] = useState(false);
  const [resendWait, startResendWait] = useCooldown();
  const [resetWait, startResetWait] = useCooldown();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Pages like a location send visitors here and expect them back after login
  const redirectTo = routerLocation.state?.from || "/";

  const showError = (err) => {
    reportAuthError(err);
    setError(authErrorMessage(err));
  };

  const rateLimited = (err) => err?.code === "auth/too-many-requests";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");
    setNotVerified(false);

    try {
      await loginUser(email, password);
      // Login may have repaired the profile after the auth listener read it
      await refreshUser();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err.code === "fqst/email-not-verified") setNotVerified(true);
      else showError(err);
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setError("");
    setMessage("");
    setSending(true);
    try {
      await resetPassword(email);
      // Firebase does not tell whether the address has an account
      setMessage(
        `Si un compte existe pour ${email.trim()}, un lien de réinitialisation vient d'y être envoyé. ` +
          "Il peut prendre quelques minutes à arriver : vérifiez aussi vos courriels indésirables (spam)."
      );
      startResetWait(RESEND_DELAY);
    } catch (err) {
      showError(err);
      if (rateLimited(err)) startResetWait(RESEND_DELAY);
    } finally {
      setSending(false);
    }
  };

  const handleResendVerification = async () => {
    setError("");
    setMessage("");
    if (!email.trim() || !password) {
      setError(
        "Entrez votre courriel et votre mot de passe, puis cliquez à nouveau sur « Renvoyer le courriel de confirmation »."
      );
      return;
    }
    setSending(true);
    try {
      const { alreadyVerified } = await resendVerificationEmail(email, password);
      if (alreadyVerified) {
        setNotVerified(false);
        setMessage("Votre adresse est déjà confirmée : vous pouvez vous connecter.");
      } else {
        setMessage(
          `Courriel de confirmation envoyé à ${email.trim()}. ` +
            "Il peut prendre quelques minutes à arriver : vérifiez aussi vos courriels indésirables (spam)."
        );
        startResendWait(RESEND_DELAY);
      }
    } catch (err) {
      showError(err);
      if (rateLimited(err)) startResendWait(RESEND_DELAY);
    } finally {
      setSending(false);
    }
  };

  const resendLabel =
    resendWait > 0
      ? `Renvoyer le courriel de confirmation (${resendWait} s)`
      : "Renvoyer le courriel de confirmation";

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-800 w-full px-4 py-8">
      <AuthHeader />

      {/* Animated Login Form */}
      <motion.div
        className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl mb-16"
        initial={{ opacity: 0, y: 50 }} // Start slightly below and invisible
        animate={{ opacity: 1, y: 0 }} // Fade in and move up
        transition={{ duration: 0.8, ease: "easeOut" }} // Smooth transition
      >
        <h2 className="text-2xl font-semibold text-center text-gray-700 mb-6">
          Connexion
        </h2>

        {error && (
          <p role="alert" className="text-red-500 text-sm text-center mb-4">
            {error}
          </p>
        )}
        {message && (
          <p role="status" className="text-green-600 text-sm text-center mb-4">
            {message}
          </p>
        )}

        {notVerified && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900">
            <p className="font-semibold mb-1">
              Votre adresse courriel n'est pas encore confirmée.
            </p>
            <p className="mb-3">
              Cliquez sur le lien envoyé à <strong>{email.trim()}</strong>. Il
              peut prendre quelques minutes à arriver : vérifiez aussi vos
              courriels indésirables (spam) ou l'onglet Promotions.
            </p>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={sending || resendWait > 0}
              className="w-full p-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition disabled:opacity-60"
            >
              {resendLabel}
            </button>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            placeholder="Courriel"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={inputClass}
          />
          <input
            type="password"
            placeholder="Mot de passe"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={inputClass}
          />

          {/* Login Button with Spinner */}
          <button
            type="submit"
            className="w-full p-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition flex items-center justify-center"
            disabled={loading}
          >
            {loading ? (
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              "Se connecter"
            )}
          </button>
        </form>

        {/* Forgot Password Link */}
        <button
          type="button"
          onClick={handlePasswordReset}
          disabled={sending || resetWait > 0}
          className="mt-3 text-blue-500 hover:underline text-sm block w-full text-center disabled:text-gray-400 disabled:no-underline"
        >
          {resetWait > 0
            ? `Mot de passe oublié ? (${resetWait} s)`
            : "Mot de passe oublié ?"}
        </button>

        {/* Resend the confirmation email */}
        {!notVerified && (
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={sending || resendWait > 0}
            className="mt-2 text-blue-500 hover:underline text-sm block w-full text-center disabled:text-gray-400 disabled:no-underline"
          >
            {resendLabel}
          </button>
        )}

        <p className="mt-4 text-center text-sm text-gray-600">
          Pas encore de compte ?{" "}
          <Link to="/signup" className="text-blue-500 hover:underline">
            Inscrivez-vous
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
